/* global fetch */
import { ToBackendKeys, ToFrontendKeys } from '@harvey-frontend/utils/utils'
import * as Sentry from '@sentry/react'
import _ from 'lodash'

import { REACT_APP_BACKEND_REST_URL } from '../utils/constants'
import { StreamingContainerKeys, StreamingResponseType } from './endpoints'

interface FetchBackendStreamRequest<T extends StreamingResponseType> {
  route: string
  token: string
  abortController: AbortController
  body: FormData | Record<string, unknown>
  responseKey: StreamingContainerKeys
  // If provided, the callback will be debounced with the given wait time.
  debounceWait?: number
  updateCallback?: (suggestion: T) => Promise<void>
}

export async function fetchBackendStream<T extends StreamingResponseType>({
  route,
  token,
  abortController,
  body,
  responseKey,
  updateCallback,
  debounceWait,
}: FetchBackendStreamRequest<T>): Promise<T[]> {
  Sentry.setTag('useXhr', 'false')
  const ENDPOINT = `${REACT_APP_BACKEND_REST_URL}/${route}`
  const headers: Record<string, string> = {
    Authorization: `Bearer ${token}`,
    Accept: 'text/event-stream',
  }

  let requestBody: string | FormData
  if (body instanceof FormData) {
    requestBody = body
  } else {
    headers['Content-Type'] = 'application/json'
    requestBody = JSON.stringify(ToBackendKeys(body))
  }

  let debouncedCallback: _.DebouncedFunc<(resp: T) => Promise<void>> | undefined
  if (updateCallback) {
    debouncedCallback = _.debounce(updateCallback, debounceWait, {
      trailing: true,
    })
  }

  const response = await fetch(ENDPOINT, {
    method: 'POST',
    headers,
    body: requestBody,
    signal: abortController.signal,
  })

  if (!response.ok || !response.body) {
    throw new Error(
      `A problem occurred while querying the backend (status code ${response.status})`
    )
  }

  const reader = response.body.getReader()
  const decoder = new TextDecoder('utf-8')
  let buffer = ''
  const data: T[] = []

  return new Promise((resolve, reject) => {
    function processStream() {
      reader
        .read()
        .then(async ({ done, value }) => {
          if (done) {
            if (buffer.length > 0) {
              Sentry.captureMessage(
                `Buffer not empty at end of stream from ${ENDPOINT}, but stream is done.`,
                'warning'
              )
            }
            if (debouncedCallback) {
              await debouncedCallback.flush()
            }
            resolve(data)
            return
          }

          buffer += decoder.decode(value, { stream: true })
          let boundaryIndex = buffer.indexOf('\n')

          while (boundaryIndex > -1) {
            const jsonString = buffer.slice(0, boundaryIndex).trim()
            buffer = buffer.slice(boundaryIndex + 1)

            if (jsonString === '') {
              boundaryIndex = buffer.indexOf('\n')
              continue
            }

            try {
              const parsed = JSON.parse(jsonString)
              let dataToUse: T
              if (responseKey === 'object') {
                dataToUse = parsed
              } else {
                dataToUse = parsed[responseKey]
              }
              data.push(dataToUse)
              if (debouncedCallback) {
                // eslint-disable-next-line @typescript-eslint/no-floating-promises
                debouncedCallback(ToFrontendKeys(dataToUse))
              }
            } catch (error) {
              console.error(
                'Error parsing streamed JSON:',
                error,
                'JSON String:',
                jsonString
              )
              Sentry.captureException(error, {
                extra: { jsonString },
              })
              reject(new Error(`Failed to parse streamed JSON: ${error}`))
              // Cancel the stream reader on error
              // eslint-disable-next-line @typescript-eslint/no-floating-promises
              reader.cancel()
              return
            }

            boundaryIndex = buffer.indexOf('\n')
          }
          processStream()
        })
        .catch((error) => {
          Sentry.captureException(error)
          reject(error)
        })
    }
    processStream()
  })
}

// Version of fetchBackendStream using XMLHttpRequest for streaming.
// This is done to avoid WebKit timeouts
export async function fetchBackendStreamXhr<T extends StreamingResponseType>({
  route,
  token,
  abortController,
  body,
  responseKey,
  updateCallback,
  debounceWait,
}: FetchBackendStreamRequest<T>): Promise<T[]> {
  Sentry.setTag('useXhr', 'true')
  const ENDPOINT = `${REACT_APP_BACKEND_REST_URL}/${route}`
  const headers: Record<string, string> = {
    Authorization: `Bearer ${token}`,
    Accept: 'text/event-stream',
  }

  let requestBody: string | FormData
  if (body instanceof FormData) {
    requestBody = body
  } else {
    headers['Content-Type'] = 'application/json'
    requestBody = JSON.stringify(ToBackendKeys(body))
  }

  let debouncedCallback: _.DebouncedFunc<(resp: T) => Promise<void>> | undefined
  if (updateCallback) {
    debouncedCallback = _.debounce(updateCallback, debounceWait, {
      trailing: true,
    })
  }

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.withCredentials = true
    xhr.timeout = 600000 // 10 minutes timeout
    xhr.open('POST', ENDPOINT, true)

    Object.entries(headers).forEach(([key, value]) => {
      xhr.setRequestHeader(key, value)
    })

    let buffer = ''
    const data: T[] = []
    let seenBytes = 0

    xhr.onprogress = () => {
      const newData = xhr.responseText.substring(seenBytes)
      buffer += newData
      seenBytes = xhr.responseText.length

      let boundaryIndex = buffer.indexOf('\n')
      while (boundaryIndex > -1) {
        const jsonString = buffer.slice(0, boundaryIndex).trim()
        buffer = buffer.slice(boundaryIndex + 1)

        if (jsonString === '') {
          boundaryIndex = buffer.indexOf('\n')
          continue
        }

        try {
          const parsed = JSON.parse(jsonString)
          let dataToUse: T
          if (responseKey === 'object') {
            dataToUse = parsed
          } else {
            dataToUse = parsed[responseKey]
          }
          data.push(dataToUse)
          if (debouncedCallback) {
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            debouncedCallback(ToFrontendKeys(dataToUse))
          }
        } catch (error) {
          console.error(
            'Error parsing streamed JSON (XHR):',
            error,
            'JSON String:',
            jsonString
          )
          Sentry.captureException(error, {
            extra: { jsonString, context: 'XHR onprogress' },
          })
          reject(new Error(`Failed to parse streamed JSON (XHR): ${error}`))
          xhr.abort()
          return
        }

        boundaryIndex = buffer.indexOf('\n')
      }
    }

    xhr.onload = async () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        // Process any remaining data in the buffer after the stream ends
        if (buffer.trim().length > 0) {
          Sentry.captureMessage(
            `Buffer not empty at end of XHR stream from ${ENDPOINT}, processing final part.`,
            'info'
          )
          try {
            const parsed = JSON.parse(buffer.trim())
            let dataToUse: T
            if (responseKey === 'object') {
              dataToUse = parsed
            } else {
              dataToUse = parsed[responseKey]
            }
            data.push(dataToUse)
            if (debouncedCallback) {
              // eslint-disable-next-line @typescript-eslint/no-floating-promises
              debouncedCallback(ToFrontendKeys(dataToUse))
            }
          } catch (error) {
            console.error(
              'Error parsing final streamed JSON chunk (XHR):',
              error,
              'Buffer:',
              buffer
            )
            Sentry.captureException(error, {
              extra: { buffer, context: 'XHR onload final parse' },
            })
          }
        }

        if (debouncedCallback) {
          await debouncedCallback.flush()
        }
        resolve(data)
      } else {
        Sentry.captureMessage(
          `XHR failed with status ${xhr.status}: ${xhr.statusText}`,
          'error'
        )
        reject(
          new Error(
            `Request failed with status ${xhr.status}: ${xhr.statusText}`
          )
        )
      }
    }

    xhr.onerror = () => {
      Sentry.captureMessage(`XHR onerror triggered for ${ENDPOINT}`, 'error')
      reject(new Error('Network request failed (XHR)'))
    }

    xhr.ontimeout = () => {
      Sentry.captureMessage(`XHR ontimeout triggered for ${ENDPOINT}`, 'error')
      reject(new Error('Request timed out (XHR)'))
    }

    xhr.onabort = () => {
      Sentry.captureMessage(`XHR onabort triggered for ${ENDPOINT}`, 'warning')
      // Check if the abort was triggered by the AbortController or something else
      if (abortController.signal.aborted) {
        reject(new Error('Request aborted by client (XHR)'))
      } else {
        reject(new Error('Request aborted unexpectedly (XHR)'))
      }
    }

    abortController.signal.addEventListener('abort', () => {
      xhr.abort()
    })

    xhr.send(requestBody)
  })
}

// https://stackoverflow.com/questions/39617221/lengthy-http-calls-failing-in-taskpane-apps-on-office-for-mac-client/40224845
// https://stackoverflow.com/questions/43789247/excel-for-mac-js-add-in-http-request-timeout
export async function fetchBackendXhr(
  route: string,
  body: FormData | Record<string, unknown>,
  token: string
): Promise<unknown> {
  const ENDPOINT = REACT_APP_BACKEND_REST_URL + '/' + route
  const promise = new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.withCredentials = true
    xhr.timeout = 600000
    xhr.open('POST', ENDPOINT, true)
    xhr.setRequestHeader('Authorization', `Bearer ${token}`)
    xhr.setRequestHeader('Content-Type', 'application/json')
    xhr.onreadystatechange = function () {
      if (xhr.readyState === 4) {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(JSON.parse(xhr.responseText))
        } else {
          reject(xhr.statusText)
        }
      }
    }
    xhr.send(JSON.stringify(body))
  })
  return promise
}
